import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Logo } from "../../components/Logo";
import { ArrowLeft, Plus, Save, Trash2, ChevronUp, ChevronDown, X } from "lucide-react";

const EMPTY = { slug: "", name: "", tagline: "", description: "", accent_color: "#4A7A3D", order: 999 };

export default function AdminCategories() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // slug or "new"
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");

  useEffect(() => { if (user === false) navigate("/admin/login"); }, [user, navigate]);

  const load = () => {
    setLoading(true);
    api.get("/admin/categories").then((r) => setCats(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { if (user && user.role === "admin") load(); }, [user]);

  const startEdit = (c) => { setEditing(c.slug); setForm({ ...EMPTY, ...c }); setError(""); };
  const startNew = () => { setEditing("new"); setForm(EMPTY); setError(""); };
  const cancel = () => { setEditing(null); setError(""); };

  const save = async () => {
    setError("");
    try {
      if (editing === "new") {
        await api.post("/admin/categories", form);
      } else {
        await api.put(`/admin/categories/${editing}`, form);
      }
      setEditing(null);
      load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Sauvegarde impossible");
    }
  };

  const del = async (slug) => {
    if (!window.confirm(`Supprimer la rubrique "${slug}" ?`)) return;
    try {
      await api.delete(`/admin/categories/${slug}`);
      load();
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail));
    }
  };

  const move = async (idx, dir) => {
    const next = [...cats];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setCats(next);
    await api.post("/admin/categories/reorder", next.map((c) => c.slug));
    load();
  };

  if (!user || user === null) return <div className="py-32 text-center font-mono-label">CHARGEMENT…</div>;

  return (
    <div className="min-h-screen bg-[#FAF8F3]" data-testid="admin-categories">
      <header className="border-b-2 border-[#1A1A18] bg-[#FAF8F3] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="font-mono-label flex items-center gap-1 hover:text-[#4A7A3D]" data-testid="categories-back"><ArrowLeft size={14} /> Retour</Link>
            <Logo size="sm" />
          </div>
          <button onClick={startNew} className="bg-[#4A7A3D] text-[#FAF8F3] px-4 py-2 font-mono-label hover:bg-[#3E6633] flex items-center gap-2" data-testid="categories-new">
            <Plus size={16} /> Nouvelle rubrique
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="font-mono-label text-[#4A7A3D] mb-2">Gestion</div>
        <h1 className="font-display text-4xl md:text-6xl leading-[1.0] mb-10">Rubriques</h1>

        {loading ? <p className="font-mono-label">CHARGEMENT…</p> : (
          <div className="border-2 border-[#1A1A18]">
            {cats.map((c, i) => (
              <div key={c.slug} className={`p-4 ${i > 0 ? "border-t border-[#E0D8CE]" : ""}`} data-testid={`category-row-${c.slug}`}>
                {editing === c.slug ? (
                  <CategoryForm form={form} setForm={setForm} onSave={save} onCancel={cancel} error={error} />
                ) : (
                  <div className="flex items-center gap-3 md:gap-4 flex-wrap">
                    <div className="flex flex-col">
                      <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1 disabled:opacity-30" aria-label="Monter"><ChevronUp size={14} /></button>
                      <button onClick={() => move(i, 1)} disabled={i === cats.length - 1} className="p-1 disabled:opacity-30" aria-label="Descendre"><ChevronDown size={14} /></button>
                    </div>
                    <div className="w-2 h-12" style={{ backgroundColor: c.accent_color || "#4A7A3D" }} />
                    <div className="flex-1 min-w-[180px]">
                      <div className="font-display text-xl md:text-2xl leading-tight">{c.name}</div>
                      <div className="font-mono-label text-[#4A7A3D]">{c.tagline}</div>
                      <div className="text-xs text-[#4B4B48] mt-0.5 break-all">/{c.slug}</div>
                    </div>
                    <button onClick={() => startEdit(c)} className="border-2 border-[#1A1A18] px-3 py-1.5 font-mono-label hover:bg-[#1A1A18] hover:text-[#FAF8F3]" data-testid={`category-edit-${c.slug}`}>Éditer</button>
                    <button onClick={() => del(c.slug)} className="border-2 border-red-700 text-red-700 px-3 py-1.5 font-mono-label hover:bg-red-700 hover:text-white" data-testid={`category-delete-${c.slug}`}><Trash2 size={14} /></button>
                  </div>
                )}
              </div>
            ))}
            {editing === "new" && (
              <div className="p-4 border-t-2 border-[#1A1A18] bg-[#F5EFEB]">
                <CategoryForm form={form} setForm={setForm} onSave={save} onCancel={cancel} error={error} isNew />
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

function CategoryForm({ form, setForm, onSave, onCancel, error, isNew }) {
  const u = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <div className="space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="font-mono-label text-[#4B4B48] block mb-1">Nom</label>
          <input value={form.name} onChange={(e) => u("name", e.target.value)} className="bg-transparent border-b-2 border-[#1A1A18] w-full py-2 font-display text-xl outline-none" data-testid="category-form-name" />
        </div>
        <div>
          <label className="font-mono-label text-[#4B4B48] block mb-1">Slug (URL)</label>
          <input value={form.slug} onChange={(e) => u("slug", e.target.value)} placeholder={isNew ? "(auto si vide)" : ""} className="bg-transparent border-b-2 border-[#1A1A18] w-full py-2 font-mono outline-none" data-testid="category-form-slug" />
        </div>
      </div>
      <div>
        <label className="font-mono-label text-[#4B4B48] block mb-1">Tagline</label>
        <input value={form.tagline} onChange={(e) => u("tagline", e.target.value)} className="bg-transparent border-b-2 border-[#1A1A18] w-full py-2 font-quote italic outline-none" data-testid="category-form-tagline" />
      </div>
      <div>
        <label className="font-mono-label text-[#4B4B48] block mb-1">Description</label>
        <textarea rows={2} value={form.description} onChange={(e) => u("description", e.target.value)} className="bg-transparent border-2 border-[#E0D8CE] w-full p-2 outline-none focus:border-[#4A7A3D]" data-testid="category-form-description" />
      </div>
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label className="font-mono-label text-[#4B4B48] block mb-1">Couleur d'accent</label>
          <input type="color" value={form.accent_color} onChange={(e) => u("accent_color", e.target.value)} className="w-20 h-10 border-2 border-[#1A1A18]" data-testid="category-form-color" />
        </div>
        <div className="flex-1 min-w-[200px]" />
        {error && <div className="text-red-700 font-mono-label">{error}</div>}
        <button onClick={onCancel} className="border-2 border-[#1A1A18] px-4 py-2 font-mono-label flex items-center gap-2" data-testid="category-form-cancel">
          <X size={14} /> Annuler
        </button>
        <button onClick={onSave} className="bg-[#4A7A3D] text-[#FAF8F3] px-4 py-2 font-mono-label flex items-center gap-2 hover:bg-[#3E6633]" data-testid="category-form-save">
          <Save size={14} /> Sauvegarder
        </button>
      </div>
    </div>
  );
}
